import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import {
  createSetting,
  deleteSetting,
  getSetting,
  listSettings,
  updateSetting,
} from "./settingsService";

const emptyPlan = {
  name: "",
  payout: "0.00",
  mourners_transport: "0.00",
  maturity_period: 0,
  require_joining_fee: false,
  joining_fee: "0.00",
  ar_customer_code: "",
  is_active: true,
};

const emptyGrade = {
  member_type: "POLICY HOLDER",
  lower_cut_off_age: 0,
  upper_cut_off_age: 0,
  premium_amount: "0.00",
  is_active: true,
};

const emptyCashGrade = {
  policy_type_setup: "",
  premium: "0.00",
  is_active: true,
};

const emptyBenefit = {
  description: "",
  cost: "0.00",
  item_id: "",
  is_active: true,
};

function IconButton({
  icon,
  title,
  className = "",
  ...props
}) {
  return (
    <button
      type="button"
      className={`settings-icon-action ${className}`}
      title={title}
      aria-label={title}
      {...props}
    >
      <i className={`bi bi-${icon}`}></i>
    </button>
  );
}

export default function PlanSetup() {
  const [plans, setPlans] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(emptyPlan);
  const [editorOpen, setEditorOpen] = useState(false);
  const [tab, setTab] = useState("details");
  const [search, setSearch] = useState("");

  const [grades, setGrades] = useState([]);
  const [cashGrades, setCashGrades] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [cashPlanTypes, setCashPlanTypes] = useState([]);

  const [grade, setGrade] = useState(emptyGrade);
  const [editingGradeId, setEditingGradeId] = useState(null);
  const [cash, setCash] = useState(emptyCashGrade);
  const [benefit, setBenefit] = useState({ item: "", cost: "" });
  const [availableItems, setAvailableItems] = useState([]);

  const loadPlans = async () => {
    setPlans(await listSettings("plans"));
  };

  const loadChildren = async (id) => {
    const [normal, cashPlan, planBenefits] = await Promise.all([
      listSettings("grades", { plan: id }),
      listSettings("cash-plan-grades", { plan: id }),
      listSettings("plan-benefits", { plan: id }),
    ]);

    setGrades(normal);
    setCashGrades(cashPlan);
    setBenefits(planBenefits);
    const itemList = await listSettings("items", { active: true });
    setAvailableItems(itemList);
  };

  useEffect(() => {
    Promise.all([
      loadPlans(),
      listSettings("cash-plan-types").then(setCashPlanTypes),
    ]).catch(() =>
      toast.error("Could not load plan setup.")
    );
  }, []);

  const filteredPlans = useMemo(() => {
    const term = search.trim().toUpperCase();

    if (!term) return plans;

    return plans.filter((plan) =>
      `${plan.code} ${plan.name}`
        .toUpperCase()
        .includes(term)
    );
  }, [plans, search]);

  const openPlan = async (plan) => {
    try {
      const full = await getSetting("plans", plan.id);
      setSelected(full);
      setForm(full);
      await loadChildren(full.id);
      setTab("details");
    } catch {
      toast.error("Could not open the selected plan.");
    }
  };

  const startNewPlan = () => {
    setSelected(null);
    setForm(emptyPlan);
    setGrades([]);
    setCashGrades([]);
    setBenefits([]);
    setEditorOpen(true);
  };

  const editSelectedPlan = () => {
    if (!selected) return;
    setForm(selected);
    setTab("details");
    setEditorOpen(true);
  };

  const savePlan = async (event) => {
    event.preventDefault();
    const creating = !selected;

    try {
      const saved = selected
        ? await updateSetting("plans", selected.id, form)
        : await createSetting("plans", form);

      await loadPlans();
      await openPlan(saved);
      setEditorOpen(true);

      toast.success(
        creating
          ? "Plan created. Complete its cut-offs, premiums and benefits."
          : "Plan details updated."
      );
    } catch (error) {
      toast.error(
        Object.values(error.response?.data || {})
          .flat()
          .join(" ") || "Could not save plan."
      );
    }
  };

  const removePlan = async (plan) => {
    if (!window.confirm(`Delete ${plan.name}?`)) return;

    try {
      await deleteSetting("plans", plan.id);

      if (selected?.id === plan.id) {
        setSelected(null);
        setForm(emptyPlan);
        setGrades([]);
        setCashGrades([]);
        setBenefits([]);
      }

      await loadPlans();
    } catch {
      toast.error(
        "This plan cannot be deleted while it is in use."
      );
    }
  };

  const childSave = async (
    type,
    data,
    reset,
    editingId = null
  ) => {
    if (!selected) {
      toast.info("Select or save a plan first.");
      return;
    }

    try {
      const payload = {
        ...data,
        plan: selected.id,
      };

      if (editingId) {
        await updateSetting(type, editingId, payload);
      } else {
        await createSetting(type, payload);
      }

      reset();
      await loadChildren(selected.id);
      toast.success(
        editingId ? "Record updated." : "Record added."
      );
    } catch (error) {
      const response = error.response?.data || {};
      toast.error(
        response.message ||
        response.detail ||
        Object.values(response).flat().join(" ") ||
        "Could not save the record."
      );
    }
  };

  const childDelete = async (type, id) => {
    if (!window.confirm("Delete this record?")) return;

    try {
      await deleteSetting(type, id);
      await loadChildren(selected.id);
    } catch {
      toast.error("Could not delete this record.");
    }
  };

  return (
    <section className="settings-panel modern-setup-panel">
      <div className="settings-panel__head">
        <div>
          <h2>Policy plans</h2>
          <p>
            Manage plans, premiums, cash-plan grades and
            included benefits.
          </p>
        </div>

        <button
          className="settings-primary-action"
          onClick={startNewPlan}
        >
          <i className="bi bi-plus-lg"></i>
          New plan
        </button>
      </div>

      <div className="setup-toolbar">
        <div className="setup-search">
          <i className="bi bi-search"></i>
          <input
            value={search}
            placeholder="Search by plan code or name"
            onChange={(event) =>
              setSearch(event.target.value)
            }
          />
        </div>

        <span className="setup-record-count">
          {filteredPlans.length} plan
          {filteredPlans.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="settings-table-wrap setup-main-table">
        <table>
          <thead>
            <tr>
              <th>Plan code</th>
              <th>Plan name</th>
              <th>Cash allowance</th>
              <th>Maturity</th>
              <th>Status</th>
              <th className="table-actions-column">
                Actions
              </th>
            </tr>
          </thead>

          <tbody>
            {filteredPlans.length === 0 ? (
              <tr>
                <td colSpan="6" className="settings-empty">
                  No policy plans found.
                </td>
              </tr>
            ) : (
              filteredPlans.map((plan) => (
                <tr
                  key={plan.id}
                  className={
                    selected?.id === plan.id
                      ? "selected-row"
                      : ""
                  }
                  onClick={() => openPlan(plan)}
                >
                  <td>
                    <span className="record-code">
                      {plan.code}
                    </span>
                  </td>
                  <td>
                    <strong>{plan.name}</strong>
                  </td>
                  <td>{plan.payout}</td>
                  <td>
                    {plan.maturity_period || 0} years
                  </td>
                  <td>
                    <span
                      className={`status-pill ${
                        plan.is_active === false
                          ? "inactive"
                          : "active"
                      }`}
                    >
                      {plan.is_active === false
                        ? "Inactive"
                        : "Active"}
                    </span>
                  </td>
                  <td
                    className="table-actions-column"
                    onClick={(event) =>
                      event.stopPropagation()
                    }
                  >
                    <IconButton
                      icon="eye"
                      title="Open plan"
                      onClick={() => openPlan(plan)}
                    />
                    <IconButton
                      icon="pencil-square"
                      title="Edit plan"
                      onClick={async () => {
                        await openPlan(plan);
                        setEditorOpen(true);
                      }}
                    />
                    <IconButton
                      icon="trash3"
                      title="Delete plan"
                      className="danger"
                      onClick={() => removePlan(plan)}
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editorOpen && selected && (
        <div
          className="complete-plan-modal-backdrop"
          onClick={() => setEditorOpen(false)}
        />
      )}

      {!selected ? (
        <div className="setup-empty-state">
          <div>
            <i className="bi bi-shield-check"></i>
          </div>
          <h3>Select a policy plan</h3>
          <p>
            Open a plan to maintain its normal grades,
            cash-plan grades and benefits.
          </p>
        </div>
      ) : (
        <div
          className={`record-detail-workspace ${
            editorOpen && selected
              ? "complete-plan-modal"
              : ""
          }`}
        >
          <div className="record-detail-header">
            <div>
              <span className="record-code">
                {selected.code}
              </span>
              <h3>{selected.name}</h3>
              <p>
                Cash allowance: {selected.payout} · Maturity:
                {" "}
                {selected.maturity_period || 0} years
              </p>
            </div>

            <button
              type="button"
              className="secondary-action"
              onClick={
                editorOpen
                  ? () => setEditorOpen(false)
                  : editSelectedPlan
              }
            >
              <i
                className={`bi bi-${
                  editorOpen ? "x-lg" : "pencil-square"
                }`}
              ></i>
              {editorOpen
                ? "Close plan editor"
                : "Edit complete plan"}
            </button>
          </div>

          <div className="plan-summary-grid">
            <SummaryCard
              label="Cash allowance"
              value={selected.payout}
              icon="cash-stack"
            />
            <SummaryCard
              label="Mourners transport"
              value={selected.mourners_transport}
              icon="truck"
            />
            <SummaryCard
              label="Joining fee"
              value={
                selected.require_joining_fee
                  ? selected.joining_fee
                  : "NOT REQUIRED"
              }
              icon="receipt"
            />
            <SummaryCard
              label="AR customer"
              value={
                selected.ar_customer_code || "NOT LINKED"
              }
              icon="person-vcard"
            />
          </div>

          <div className="modern-tabs">
            <button
              className={tab === "details" ? "active" : ""}
              onClick={() => {
                setForm(selected);
                setTab("details");
              }}
            >
              <i className="bi bi-sliders"></i>
              1. Basic details
            </button>

            <button
              className={tab === "grades" ? "active" : ""}
              onClick={() => setTab("grades")}
            >
              <i className="bi bi-people"></i>
              2. Age cut-offs & premiums
              <span>{grades.length}</span>
            </button>

            <button
              className={tab === "cash" ? "active" : ""}
              onClick={() => setTab("cash")}
            >
              <i className="bi bi-calendar3"></i>
              3. Cash-plan options
              <span>{cashGrades.length}</span>
            </button>

            <button
              className={
                tab === "benefits" ? "active" : ""
              }
              onClick={() => setTab("benefits")}
            >
              <i className="bi bi-gift"></i>
              4. Benefits
              <span>{benefits.length}</span>
            </button>
          </div>

          {tab === "details" && (
            <PlanEditorModal
              selected={selected}
              form={form}
              setForm={setForm}
              onClose={() => setTab("grades")}
              onSave={savePlan}
              embedded
            />
          )}

          {tab === "grades" && (
            <ChildWorkspace
              title="Normal policy grades"
              subtitle="Set member types, age ranges and monthly premiums."
              columns={[
                "Member type",
                "Lower cutoff age",
                "Upper cutoff age",
                "Premium",
              ]}
              rows={grades.map((item) => [
                item.member_type,
                item.lower_cut_off_age,
                item.upper_cut_off_age,
                item.premium_amount,
              ])}
              ids={grades.map((item) => item.id)}
              onEdit={(id) => {
                const item = grades.find(
                  (entry) => entry.id === id
                );
                if (!item) return;
                setEditingGradeId(item.id);
                setGrade({
                  member_type: item.member_type,
                  lower_cut_off_age:
                    item.lower_cut_off_age,
                  upper_cut_off_age:
                    item.upper_cut_off_age,
                  premium_amount: item.premium_amount,
                  is_active:
                    item.is_active !== false,
                });
              }}
              onDelete={(id) =>
                childDelete("grades", id)
              }
            >
              <select
                value={grade.member_type}
                onChange={(event) =>
                  setGrade({
                    ...grade,
                    member_type: event.target.value,
                  })
                }
              >
                <option>POLICY HOLDER</option>
                <option>SPOUSE</option>
                <option>CHILD</option>
                <option>OTHER DEPENDANT</option>
              </select>

              <input
                type="number"
                min="0"
                placeholder="Lower age"
                value={grade.lower_cut_off_age}
                onChange={(event) =>
                  setGrade({
                    ...grade,
                    lower_cut_off_age:
                      event.target.value,
                  })
                }
              />

              <input
                type="number"
                min="0"
                placeholder="Upper age"
                value={grade.upper_cut_off_age}
                onChange={(event) =>
                  setGrade({
                    ...grade,
                    upper_cut_off_age:
                      event.target.value,
                  })
                }
              />

              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="Premium"
                value={grade.premium_amount}
                onChange={(event) =>
                  setGrade({
                    ...grade,
                    premium_amount:
                      event.target.value,
                  })
                }
              />

              <button
                onClick={() =>
                  childSave(
                    "grades",
                    grade,
                    () => {
                      setGrade(emptyGrade);
                      setEditingGradeId(null);
                    },
                    editingGradeId
                  )
                }
              >
                <i
                  className={`bi bi-${
                    editingGradeId
                      ? "check2-circle"
                      : "plus-lg"
                  }`}
                ></i>
                {editingGradeId
                  ? "Update grade"
                  : "Add grade"}
              </button>

              {editingGradeId && (
                <button
                  type="button"
                  className="secondary-child-action"
                  onClick={() => {
                    setEditingGradeId(null);
                    setGrade(emptyGrade);
                  }}
                >
                  Cancel edit
                </button>
              )}
            </ChildWorkspace>
          )}

          {tab === "cash" && (
            <ChildWorkspace
              title="Cash-plan policy grades"
              subtitle="Set fixed-term cash-plan options and premiums."
              columns={[
                "Policy type",
                "Period in months",
                "Premium",
              ]}
              rows={cashGrades.map((item) => [
                item.policy_type_name || item.policy_type,
                item.period_months,
                item.premium,
              ])}
              ids={cashGrades.map((item) => item.id)}
              onDelete={(id) =>
                childDelete("cash-plan-grades", id)
              }
            >
              <select
                value={cash.policy_type_setup}
                onChange={(event) =>
                  setCash({
                    ...cash,
                    policy_type_setup: event.target.value,
                  })
                }
              >
                <option value="">SELECT POLICY TYPE...</option>
                {cashPlanTypes
                  .filter((item) => item.is_active !== false)
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
              </select>

              <div className="cash-type-period-preview">
                <span>Period</span>
                <strong>
                  {cashPlanTypes.find(
                    (item) => String(item.id) === String(cash.policy_type_setup)
                  )?.period_months || 0} MONTHS
                </strong>
              </div>

              <input
                type="number"
                min="0"
                step="0.01"
                placeholder="Premium"
                value={cash.premium}
                onChange={(event) =>
                  setCash({
                    ...cash,
                    premium: event.target.value,
                  })
                }
              />

              <button
                onClick={() =>
                  childSave(
                    "cash-plan-grades",
                    cash,
                    () => setCash(emptyCashGrade)
                  )
                }
              >
                <i className="bi bi-plus-lg"></i>
                Add cash grade
              </button>
            </ChildWorkspace>
          )}

          {tab === "benefits" && (
            <ChildWorkspace
              title="Plan benefits"
              subtitle="Select configured stock items or service benefits from Item Master."
              columns={["Benefit code", "Benefit description", "Item type", "Cost"]}
              rows={benefits.map((item) => [item.benefit_code, item.description, item.item_type || "—", item.cost])}
              ids={benefits.map((item) => item.id)}
              onDelete={(id) => childDelete("plan-benefits", id)}
            >
              <select
                value={benefit.item}
                onChange={(event) => {
                  const item = availableItems.find(
                    (entry) => String(entry.id) === event.target.value
                  );
                  setBenefit({
                    item: event.target.value,
                    cost: item?.unit_price || "0.00",
                  });
                }}
              >
                <option value="">SELECT ITEM...</option>
                {availableItems.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.code} — {item.name} — {item.unit_price}
                  </option>
                ))}
              </select>
              <input
                type="number"
                min="0"
                step="0.01"
                value={benefit.cost}
                placeholder="Plan benefit cost"
                onChange={(event) =>
                  setBenefit({ ...benefit, cost: event.target.value })
                }
              />
              <div className="benefit-selection-note">
                <i className="bi bi-link-45deg"></i>
                Item Master suggests the cost. Confirm or adjust it for this plan.
              </div>
              <button
                disabled={!benefit.item}
                onClick={() =>
                  childSave("plan-benefits", benefit, () =>
                    setBenefit({ item: "", cost: "" })
                  )
                }
              >
                <i className="bi bi-plus-lg"></i>Add benefit
              </button>
            </ChildWorkspace>
          )}

        </div>
      )}

      {editorOpen && !selected && (
        <PlanEditorModal
          selected={selected}
          form={form}
          setForm={setForm}
          onClose={() => setEditorOpen(false)}
          onSave={savePlan}
        />
      )}
    </section>
  );
}

function SummaryCard({ label, value, icon }) {
  return (
    <div className="summary-card">
      <i className={`bi bi-${icon}`}></i>
      <div>
        <span>{label}</span>
        <strong>{value ?? "—"}</strong>
      </div>
    </div>
  );
}

function ChildWorkspace({
  title,
  subtitle,
  columns,
  rows,
  ids,
  onEdit,
  onDelete,
  children,
}) {
  return (
    <section className="child-workspace">
      <div className="child-workspace__heading">
        <h4>{title}</h4>
        <p>{subtitle}</p>
      </div>

      <div className="child-entry-form">
        {children}
      </div>

      <div className="settings-table-wrap">
        <table>
          <thead>
            <tr>
              {columns.map((column) => (
                <th key={column}>{column}</th>
              ))}
              <th className="table-actions-column">
                Action
              </th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  className="settings-empty"
                >
                  No records added.
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr key={ids[index]}>
                  {row.map((value, cellIndex) => (
                    <td key={cellIndex}>{value}</td>
                  ))}
                  <td className="table-actions-column">
                    {onEdit && (
                      <IconButton
                        icon="pencil-square"
                        title="Edit"
                        onClick={() =>
                          onEdit(ids[index])
                        }
                      />
                    )}
                    <IconButton
                      icon="trash3"
                      title="Remove"
                      className="danger"
                      onClick={() =>
                        onDelete(ids[index])
                      }
                    />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function PlanEditorModal({
  selected,
  form,
  setForm,
  onClose,
  onSave,
  embedded = false,
}) {
  return (
    <div
      className={embedded ? "inline-plan-editor" : "setup-modal-backdrop"}
      onMouseDown={embedded ? undefined : onClose}
    >
      <div
        className={embedded ? "setup-modal setup-modal--inline" : "setup-modal"}
        onMouseDown={(event) =>
          event.stopPropagation()
        }
      >
        <div className="setup-modal__header">
          <div>
            <span>
              {selected ? selected.code : "NEW PLAN"}
            </span>
            <h3>
              {selected
                ? "Update basic plan details"
                : "Create policy plan"}
            </h3>
          </div>

          <button
            type="button"
            className="modal-close"
            onClick={onClose}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        <form
          className="modern-editor-form"
          onSubmit={onSave}
        >
          <div className="plan-editor-guidance">
            <i className="bi bi-info-circle"></i>
            <div>
              <strong>Step 1: Basic plan details</strong>
              <p>
                Save the plan name, allowance, maturity and joining-fee settings here.
                Use steps 2–4 in the plan workspace for age cut-offs, premiums,
                cash-plan options and benefits.
              </p>
            </div>
          </div>
          <label className="editor-wide">
            <span>Plan name</span>
            <input
              value={form.name || ""}
              onChange={(event) =>
                setForm({
                  ...form,
                  name: event.target.value.toUpperCase(),
                })
              }
              required
            />
          </label>

          <label>
            <span>Cash allowance</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.payout ?? 0}
              onChange={(event) =>
                setForm({
                  ...form,
                  payout: event.target.value,
                })
              }
            />
          </label>

          <label>
            <span>Mourners transport fee</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.mourners_transport ?? 0}
              onChange={(event) =>
                setForm({
                  ...form,
                  mourners_transport:
                    event.target.value,
                })
              }
            />
          </label>

          <label>
            <span>Maturity period (years)</span>
            <input
              type="number"
              min="0"
              value={form.maturity_period ?? 0}
              onChange={(event) =>
                setForm({
                  ...form,
                  maturity_period: event.target.value,
                })
              }
            />
          </label>

          <label>
            <span>Joining fee required</span>
            <select
              value={
                form.require_joining_fee ? "YES" : "NO"
              }
              onChange={(event) =>
                setForm({
                  ...form,
                  require_joining_fee:
                    event.target.value === "YES",
                })
              }
            >
              <option>NO</option>
              <option>YES</option>
            </select>
          </label>

          <label>
            <span>Joining fee amount</span>
            <input
              type="number"
              step="0.01"
              min="0"
              disabled={!form.require_joining_fee}
              value={form.joining_fee ?? 0}
              onChange={(event) =>
                setForm({
                  ...form,
                  joining_fee: event.target.value,
                })
              }
            />
          </label>

          <label>
            <span>AR customer link</span>
            <input
              value={form.ar_customer_code || ""}
              placeholder="Optional customer code"
              onChange={(event) =>
                setForm({
                  ...form,
                  ar_customer_code:
                    event.target.value.toUpperCase(),
                })
              }
            />
          </label>

          <div className="setup-modal__actions">
            <button
              type="button"
              className="secondary-action"
              onClick={onClose}
            >
              Cancel
            </button>

            <button type="submit">
              <i className="bi bi-check2-circle"></i>
              {selected ? "Update plan" : "Save plan"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
